import { useState, type FormEvent, type JSX } from 'react';
import type { TFunction } from 'i18next';
import { ApiError, NetworkError } from '../../shared/api/client';
import { useTranslation } from '../../shared/i18n';
import { describeError } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { Field } from '../../shared/ui/Field';
import { BrandMark } from '../../shared/ui/BrandMark';
import { Signature } from '../../shared/ui/Signature';
import { useLogin } from './api';
import { DemoMap } from './DemoMap';

/**
 * Экран входа по макету NetDeck.html, раздел «02 · Вход».
 *
 * Слева форма, справа штриховка макета с демо-схемой сети поверх неё.
 * Сообщения об ошибках следуют §12: говорят, что произошло, и не
 * подсказывают, существует ли такой адрес.
 */
export function LoginPage(): JSX.Element {
  const { t } = useTranslation();

  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    login.mutate({ email, password });
  }

  return (
    <main className="grid min-h-[100dvh] grid-cols-1 bg-surface lg:grid-cols-2">
      <div className="flex flex-col px-8 pt-12 pb-6 lg:px-24">
        <div className="flex flex-1 flex-col justify-center gap-7 pb-6">
          {/*
            Знак и название крупнее, чем в шапке оболочки: там они соседствуют
            с меню, а здесь стоят над заголовком в 24 px и в прежнем размере
            терялись на его фоне.
          */}
          <div className="flex items-center gap-3">
            <BrandMark size={48} />
            <div className="text-[28px] font-semibold tracking-[0.02em]">NetDeck</div>
          </div>

          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-semibold tracking-[-0.01em]">{t('auth.login.title')}</h1>
            <p className="text-base text-muted">{t('auth.login.subtitle')}</p>
          </div>

          <form className="flex max-w-[380px] flex-col gap-3.5" onSubmit={submit} noValidate>
            <Field
              label={t('auth.login.email')}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              mono
              placeholder="user@example.org"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <Field
              label={t('auth.login.password')}
              type="password"
              reveal
              name="password"
              autoComplete="current-password"
              required
              mono
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {login.error ? <LoginError error={login.error} /> : null}

            <Button icon="login" type="submit" variant="primary" disabled={login.isPending}>
              {login.isPending ? t('auth.login.checking') : t('auth.login.submit')}
            </Button>

            <p className="font-mono text-xs text-dim">{t('auth.login.resetHint')}</p>
          </form>
        </div>

        {/*
          Та же подпись, что внизу меню оболочки. Прижата к низу колонки,
          а не стоит под формой: форма центрирована по высоте, и подпись
          рядом с ней читалась бы частью формы.
        */}
        <Signature />
      </div>

      <div className="relative hidden flex-col border-l border-line bg-canvas p-10 lg:flex">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, var(--nd-canvas-grid) 0 2px, var(--nd-canvas) 2px 9px)',
          }}
        />

        {/*
          Демо-схема вместо прежней заглушки: до входа человек всё равно
          ничего не увидит, а показать, чем платформа занимается, лучше
          сразу. Штриховка макета остаётся фоном — схема лежит поверх неё.
        */}
        <DemoMap className="relative min-h-0 flex-1" />

        <p className="relative mt-6 font-mono text-xs text-faint">{t('auth.login.demoHint')}</p>
      </div>
    </main>
  );
}

/**
 * §12: сообщение говорит, что произошло и что делать.
 *
 * Причину отказа не уточняем: одинаковый текст для неверного пароля и
 * несуществующего адреса — часть защиты от перебора, и интерфейс не должен
 * сводить её на нет.
 */
function LoginError({ error }: { error: Error }): JSX.Element {
  const { t } = useTranslation();

  const message =
    error instanceof NetworkError
      ? t('auth.login.noConnection')
      : error instanceof ApiError && error.code === 'RATE_LIMITED'
        ? t('auth.login.tooManyAttempts', { hint: retryHint(error, t) })
        : error instanceof ApiError && error.status === 401
          ? t('auth.login.badCredentials')
          : describeError(error, t);

  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-xs border-l-2 border-l-warn bg-overlay px-2.5 py-2"
    >
      <span className="text-sm leading-[1.45] text-muted">{message}</span>
    </div>
  );
}

function retryHint(error: ApiError, t: TFunction): string {
  const seconds = error.details?.['retryAfterSec'];

  return typeof seconds === 'number'
    ? t('auth.login.retryInSeconds', { seconds })
    : t('auth.login.retryInAMinute');
}
